<template>
  <div
    data-ui="added-to-cart-container"
    data-analytics-config="exclude-all"
  >
    <cdr-modal
      :opened="isOpen"
      label="Added to Cart"
      aria-describedby="description"
      @closed="handleClose"
    >
      <template #title>
        <cdr-text
          tag="h2"
          class="atc-modal__heading"
        >
          Added to Cart
        </cdr-text>
      </template>
      <section
        class="atc-modal"
        data-ui="added-to-cart-modal"
      >
        <section class="atc-modal__product-details">
          <div class="atc-modal__img-and-summary">
            <div class="atc-modal__image-wrapper">
              <cdr-img
                class="atc-modal__product-image"
                src="/assets/membership/product/product-image/live.svg"
              />
            </div>
            <div class="atc-modal__description">
              <cdr-text tag="span">
                REI Co-op
              </cdr-text>
              <cdr-text tag="span">
                Lifetime Membership
              </cdr-text>
              <cdr-text tag="span">
                Price: ${{ computedMembershipPrice }}
              </cdr-text>
            </div>
          </div>
          <cdr-text class="atc-modal__total">
            Cart subtotal: ${{ cartSubtotal }}
            <span class="atc-modal__item-count">
              {{ computedTotalQuantity }}
            </span>
          </cdr-text>
          <div class="atc-modal__actions">
            <cdr-button
              :href="ctaHref"
              class="atc-modal__action"
              data-ui="go-to-cart-text"
              tag="a"
              @click="handleClick"
            >
              {{ goToCartText }}
            </cdr-button>
            <cdr-button
              class="atc-modal__action"
              modifier="secondary"
              @click="handleClose"
            >
              Continue Shopping
            </cdr-button>
          </div>
        </section>
      </section>
    </cdr-modal>
  </div>
</template>

<script>
import {
  CdrButton,
  CdrImg,
  CdrModal,
  CdrText,
} from '@rei/cedar';
import CartController from '../../../utils/cartController';
import { metricsLink } from '../../../utils/analytics';

export default {
  name: 'AddedToCartModal',
  components: {
    CdrButton,
    CdrImg,
    CdrModal,
    CdrText,
  },
  inject: ['homePageInjectables'],
  props: {
    isOpenOverride: {
      type: Boolean,
      required: false,
      default: false,
    },
    location: {
      type: String,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      cartCount: 0,
      cartSubtotal: 0, // starting amount is the retail price of a single membership
      Cart: null,
      isOpen: this.isOpenOverride,
      userLoggedIn: false,
    };
  },
  computed: {
    /*
     * "Checkout" value for any user getting jumped forward past cart
     */
    analyticsType() {
      return this.cartCount === 1 ? 'checkout' : 'cart';
    },
    /*
     * Dynamically update description of primary CTA based on whether user has items in cart
     */
    goToCartText() {
      return `${this.cartCount === 1 ? '' : 'Cart & '}Checkout`;
    },
    goToCartAnalyticsId() {
      const { analyticsType, location: page, goToCartText } = this;
      return `rei:${page}_${analyticsType}_modal_${goToCartText}`;
    },
    /*
     * Send user to the furthest possible step in the checkout based on their login status
     */
    ctaHref() {
      return this.cartCount === 1 ? '/CheckoutLoginView' : '/ShoppingCart';
    },
    computedMembershipPrice() {
      if (!this.homePageInjectables.addMembershipToCart) return 30;
      // turn this cta string (which contains the current price) into a number.
      // price may contain a decimal.
      const price = this.homePageInjectables.addMembershipToCart.replace(/[^0-9.]+/g, '');
      return parseFloat(price);
    },
    computedTotalQuantity() {
      const { cartCount } = this;
      return `(${cartCount} item${cartCount === 1 ? '' : 's'})`;
    },
  },
  watch: {
    /*
     * When opened, check on the cart quantities and total
     */
    isOpenOverride(isOpen) {
      // don't hit any services if override is called while modal is not open
      if (!isOpen) return;

      this.updateCart()
        .then(() => {
          this.isOpen = isOpen;
          /* c8 ignore start */
          if (isOpen) {
            const { analyticsType, location } = this;
            metricsLink(`rei:${location}_${analyticsType}_modal_open`);
          }
          /* c8 ignore end */
        });
    },
  },
  mounted() {
    this.Cart = this.Cart || new CartController();
    this.cartSubtotal = this.computedMembershipPrice;
  },
  methods: {
    async updateCart() {
      const count = await this.Cart.getCartCount();
      this.cartCount = count;

      // membership is the only thing in cart
      if (count === 1) return Promise.resolve();

      // there are other items in the cart, get subtotal
      return this.Cart.auditCart()
        .then(() => this.Cart.getCart())
        .then((data) => {
          const { order, userLoggedIn } = data;
          const { totalProduct } = order;
          this.cartSubtotal = totalProduct;
          this.userLoggedIn = userLoggedIn;
        });
    },
    handleClose() {
      this.$emit('close');
      this.isOpen = false;
      const { analyticsType, location: page } = this;
      /* c8 ignore next */
      metricsLink(`rei:${page}_${analyticsType}_modal_Continue Shopping`);
    },
    /* c8 ignore start */
    handleClick() {
      const { analyticsType, location: page, goToCartText } = this;
      metricsLink(`rei:${page}_${analyticsType}_modal_${goToCartText}`);
    },
    /* c8 ignore end */
  },
};
</script>

<style lang="scss">

.atc-modal__img-and-summary {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 14rem 1fr;
  grid-column-gap: $cdr-space-one-x;
  min-height: 190px;
}

.atc-modal__heading {
  @include cdr-text-heading-serif-strong-600;
}

.atc-modal__description {
  display: flex;
  flex-direction: column;
}

.atc-modal__total {
  @include cdr-text-utility-sans-strong-300;
  margin-bottom: $cdr-space-one-and-a-half-x;
}

.atc-modal__item-count {
  font-weight: $cdr-text-utility-sans-300-weight;
  // margin-right: $cdr-space-quarter-x;
}
.atc-modal__actions {
  @include cdr-sm-mq-up {
    display: grid;
    grid-auto-flow: column;
    grid-gap: $cdr-space-one-x;
  }
}

.atc-modal__action {
  display: block;
  width: 100%;
  text-align: center;
  &:first-child {
    margin-bottom: $cdr-space-one-x;
    @include cdr-sm-mq-up {
      margin-bottom: initial;
    }
  }
}

</style>
